import { useMemo, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { Store, Menu, X } from 'lucide-react';

import { useAuth } from '../auth/AuthProvider.jsx';
import { ApiError } from '../api/client.js';
import { env } from '../lib/env.js';
import { GlobalSearch } from '../components/GlobalSearch.jsx';
import { SELLER_NAV } from './sellerNav.js';
import { NavGroups, UserMenu, flattenItems, findActive } from './sidebarShell.jsx';

// Provenances the employee shell can actually open: customer-bound records
// live under /employee/customers, products open the inventory filtered by SKU,
// and knowledge documents open the read-only employee knowledge base.
// Catalogue/system targets (brand, category, store) remain admin-only.
const EMPLOYEE_SEARCH_TYPES = ['customer', 'pet', 'consultation', 'product', 'knowledge'];

/**
 * Active store indicator and switcher. A seller assigned to several stores can
 * change the store the session operates on without logging out. Rendered in the
 * sidebar footer above the user menu.
 */
function StoreSwitcher() {
  const { storeSelection, activeStoreId, selectStore } = useAuth();
  const [busy, setBusy] = useState(false);
  const stores = (storeSelection && storeSelection.stores) || [];
  if (!storeSelection || !storeSelection.scoped || stores.length === 0) return null;

  if (stores.length === 1) {
    return (
      <span className="flex items-center gap-1.5 px-2 text-xs text-sidebar-ink-muted">
        <Store className="h-3.5 w-3.5" strokeWidth={1.75} />
        {stores[0].name}
      </span>
    );
  }

  const onChange = async (e) => {
    const id = e.target.value;
    if (!id || id === activeStoreId) return;
    setBusy(true);
    try {
      await selectStore(id);
      toast.success('Negozio attivo aggiornato.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Impossibile cambiare negozio.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <label className="flex items-center gap-1.5 rounded-md border border-sidebar-border bg-sidebar-elevated px-2 py-1">
      <Store className="h-3.5 w-3.5 shrink-0 text-sidebar-ink-muted" strokeWidth={1.75} />
      <select
        value={activeStoreId || ''}
        onChange={onChange}
        disabled={busy}
        aria-label="Negozio attivo"
        className="w-full bg-transparent text-xs text-sidebar-ink focus:outline-none"
      >
        {stores.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
      </select>
    </label>
  );
}

export function EmployeeLayout() {
  const { user, role, hasPermission, logout, storeSelection } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // The seller's own store(s): customer search results from these stores are
  // marked as "own" while customers of other branches are shown as such.
  const ownStoreIds = useMemo(
    () => ((storeSelection && storeSelection.stores) || []).map((s) => s._id),
    [storeSelection]
  );

  // The chat is a full-height app surface: it manages its own scroll and
  // padding, so the shell drops the default padded/scrolling main for it.
  const isChat = location.pathname.startsWith('/employee/chat');

  const flat = useMemo(() => flattenItems(SELLER_NAV), []);
  const active = useMemo(() => findActive(flat, location.pathname), [flat, location.pathname]);

  const onLogout = async () => { await logout(); navigate('/login', { replace: true }); };
  const closeMobileNav = () => setMobileNavOpen(false);

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-surface">
      {mobileNavOpen && (
        <button
          type="button"
          aria-label="Chiudi menu"
          onClick={() => setMobileNavOpen(false)}
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
        />
      )}

      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-ink transition-transform duration-200 ease-out lg:static lg:z-auto lg:translate-x-0',
          mobileNavOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex items-center gap-2 px-4 py-4">
          <span className="flex h-7 w-7 items-center justify-center rounded-md border border-sidebar-border bg-white">
            <img src="/favicon.png" alt={env.appName} className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold tracking-tight text-sidebar-ink">{env.appName}</p>
            <p className="text-[11px] text-sidebar-ink-muted">Area dipendente</p>
          </div>
          <button
            type="button"
            aria-label="Chiudi menu"
            onClick={() => setMobileNavOpen(false)}
            className="ml-auto rounded-md p-1 text-sidebar-ink-muted hover:bg-sidebar-elevated hover:text-sidebar-ink lg:hidden"
          >
            <X className="h-5 w-5" strokeWidth={1.75} />
          </button>
        </div>
        <div className="border-t border-sidebar-border" />

        {hasPermission('search:global') && (
          <div className="px-2 pt-2">
            <GlobalSearch basePath="/employee" variant="sidebar" types={EMPLOYEE_SEARCH_TYPES} ownStoreIds={ownStoreIds} />
          </div>
        )}

        <NavGroups nav={SELLER_NAV} hasPermission={hasPermission} onNavigate={closeMobileNav} />

        <div className="border-t border-sidebar-border p-2">
          <UserMenu
            user={user}
            role={role}
            accountPath="/employee/account"
            onLogout={onLogout}
            extra={<StoreSwitcher />}
          />
        </div>
      </aside>

      <main className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="flex h-12 items-center gap-3 border-b border-border bg-surface-elevated px-4 sm:px-6 lg:hidden">
          <button
            type="button"
            aria-label="Apri menu"
            onClick={() => setMobileNavOpen(true)}
            className="-ml-1 rounded-md p-1 text-ink-muted hover:bg-surface-subtle hover:text-ink"
          >
            <Menu className="h-5 w-5" strokeWidth={1.75} />
          </button>
          <span className="text-sm font-medium text-ink">{active ? active.label : 'Area dipendente'}</span>
        </header>

        <div className={clsx('min-h-0 flex-1', isChat ? 'overflow-hidden' : 'overflow-y-auto')}>
          {isChat ? (
            <Outlet />
          ) : (
            // Keyed on the path so each navigation remounts and replays the
            // fade-in, matching the admin shell's page transitions.
            <div key={location.pathname} className="animate-fade-in px-4 py-6 sm:px-6 lg:px-8">
              <Outlet />
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
